const MillisPerDay = 24 * 60 * 60 * 1000;

/**
 * An expiration instant from Firestore (`toMillis`), Algolia (epoch millis),
 * or a `Date`.
 */
export type ExpirationInstant = { toMillis(): number } | number | Date;

/**
 * Keeps a positive day count from remote config. Non-positive or non-finite
 * values fall back so a bad template cannot disable or zero-out extension.
 */
export function positiveDayCount(value: number, fallback: number): number {
	if (!Number.isFinite(value) || value <= 0) return fallback;
	return value;
}

export function toExpirationMillis(expiresAt: ExpirationInstant): number {
	if (typeof expiresAt === 'number') return expiresAt;
	if (expiresAt instanceof Date) return expiresAt.getTime();
	return expiresAt.toMillis();
}

/**
 * Last millisecond of the local calendar day. Expiration is a date, not a time.
 */
export function endOfExpirationDay(date: Date): Date {
	const end = new Date(date.getTime());
	end.setHours(23, 59, 59, 999);
	return end;
}

/**
 * Calendar date `days` after `from`, at the end of that local day.
 */
export function determineExpirationDate(from: Date, days: number): Date {
	if (!Number.isFinite(days) || days <= 0) {
		throw new Error('Expiration days must be greater than zero');
	}

	const date = new Date(from.getTime());
	date.setDate(date.getDate() + days);
	return endOfExpirationDay(date);
}

function startOfLocalDay(date: Date): Date {
	return new Date(date.getFullYear(), date.getMonth(), date.getDate());
}

/**
 * Whole local calendar days from `from` until `to`. Daylight-saving
 * boundaries are rounded so a day is never 0 or 2.
 */
function calendarDaysUntil(from: Date, to: Date): number {
	const ms = startOfLocalDay(to).getTime() - startOfLocalDay(from).getTime();
	return Math.round(ms / MillisPerDay);
}

/**
 * Whole local calendar days from today until the expiration day.
 * `0` means the analysis expires today. The time of day does not change the count.
 */
export function daysUntilExpiration(
	expiresAt: ExpirationInstant,
	now: Date = new Date(),
): number {
	return calendarDaysUntil(now, new Date(toExpirationMillis(expiresAt)));
}

/**
 * Start of the local calendar day on which an expiration first enters the
 * warning window. A warning sent at or after this instant already covers the
 * current expiration.
 */
export function expirationWarningWindowStart(
	expiresAt: ExpirationInstant,
	thresholdDays: number,
): Date {
	if (!Number.isFinite(thresholdDays) || thresholdDays <= 0) {
		throw new Error('Threshold days must be greater than zero');
	}

	const start = startOfLocalDay(new Date(toExpirationMillis(expiresAt)));
	start.setDate(start.getDate() - thresholdDays);
	return start;
}

/**
 * True when the analysis is still active and its expiration calendar day is
 * within `thresholdDays` of today. Already-expired analyses are outside the
 * window. The time of day does not change the result.
 */
export function isWithinExpirationExtensionWindow(
	expiresAt: ExpirationInstant,
	thresholdDays: number,
	now: Date = new Date(),
): boolean {
	if (!Number.isFinite(thresholdDays) || thresholdDays <= 0) return false;

	const expiresMs = toExpirationMillis(expiresAt);
	if (expiresMs <= now.getTime()) return false;

	return calendarDaysUntil(now, new Date(expiresMs)) <= thresholdDays;
}

/**
 * Adds `extensionDays` calendar days to the current expiration and rounds to
 * the end of that local day.
 */
export function extendExpirationDate(
	expiresAt: ExpirationInstant,
	extensionDays: number,
): Date {
	if (!Number.isFinite(extensionDays) || extensionDays <= 0) {
		throw new Error('Extension days must be greater than zero');
	}

	return determineExpirationDate(
		new Date(toExpirationMillis(expiresAt)),
		extensionDays,
	);
}
